<template>
    <b-container id="logs-page" v-if="$can('view-all')" fluid>
        <b-row class="page-title">
            <b-container fluid>
                <b-row>
                    <b-col cols="8">
                        <h3>Logs</h3>
                    </b-col>
                </b-row>
                <hr />
            </b-container>
        </b-row>

        <b-row class="mb-3">
            <b-col cols="12" md="3">
                <b-form-group label="Date" label-for="log-date" label-size="sm">
                    <b-form-datepicker
                        id="log-date"
                        v-model="filters.date"
                        size="sm"
                        locale="en"
                        today-button
                        reset-button
                        @input="refreshLogs"
                    ></b-form-datepicker>
                </b-form-group>
            </b-col>
            <b-col cols="12" md="3">
                <b-form-group label="Type" label-for="log-type" label-size="sm">
                    <b-form-select
                        id="log-type"
                        v-model="filters.type"
                        size="sm"
                        :options="typeOptions"
                        @change="refreshLogs"
                    ></b-form-select>
                </b-form-group>
            </b-col>
            <b-col cols="12" md="2">
                <b-form-group label="Per page" label-for="log-per-page" label-size="sm">
                    <b-form-select
                        id="log-per-page"
                        v-model="table.perPage"
                        size="sm"
                        :options="table.pageOptions"
                        @change="refreshLogs"
                    ></b-form-select>
                </b-form-group>
            </b-col>
        </b-row>

        <b-alert v-if="truncated" show variant="warning" class="mb-3">
            Showing recent logs from the last 10 MB of the legacy log file. Older entries for this date may not appear until daily log rotation is used.
        </b-alert>

        <b-row>
            <b-col cols="12">
                <b-table
                    id="logs-table"
                    :items="logsProvider"
                    :fields="fields"
                    :busy.sync="table.isBusy"
                    hover
                    responsive
                    striped
                    selectable
                    select-mode="single"
                    :per-page="table.perPage"
                    :current-page.sync="table.currentPage"
                    @row-selected="onRowSelected"
                    class="logs-table"
                >
                    <template v-slot:table-busy>
                        <div class="text-center text-danger my-2">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong>Loading...</strong>
                        </div>
                    </template>

                    <template v-slot:cell(datetime)="row">
                        {{ formatDatetime(row.item.datetime) }}
                    </template>

                    <template v-slot:cell(level)="row">
                        <b-badge :variant="levelVariant(row.item.level)">
                            {{ row.item.level }}
                        </b-badge>
                    </template>

                    <template v-slot:cell(type)="row">
                        <b-badge variant="secondary">{{ row.item.type }}</b-badge>
                    </template>

                    <template v-slot:cell(source)="row">
                        <span v-if="row.item.source">{{ row.item.source }}</span>
                        <span v-else class="text-muted">—</span>
                    </template>

                    <template v-slot:cell(message)="row">
                        <span class="log-message-preview">{{ truncateMessage(row.item.message) }}</span>
                    </template>
                </b-table>

                <b-pagination
                    v-model="table.currentPage"
                    :total-rows="table.totalRows"
                    :per-page="table.perPage"
                    aria-controls="logs-table"
                    class="mt-3"
                ></b-pagination>
            </b-col>
        </b-row>

        <b-modal
            id="log-detail-modal"
            title="Log Details"
            size="xl"
            scrollable
            @hidden="selectedLog = null"
        >
            <div v-if="selectedLog">
                <b-row class="mb-3">
                    <b-col cols="12" md="4">
                        <strong>Date/Time:</strong> {{ formatDatetime(selectedLog.datetime) }}
                    </b-col>
                    <b-col cols="12" md="4">
                        <strong>Level:</strong>
                        <b-badge :variant="levelVariant(selectedLog.level)" class="ml-1">
                            {{ selectedLog.level }}
                        </b-badge>
                    </b-col>
                    <b-col cols="12" md="4">
                        <strong>Type:</strong> {{ selectedLog.type }}
                    </b-col>
                </b-row>

                <div v-if="selectedLog.source" class="mb-3">
                    <strong>Source:</strong> {{ selectedLog.source }}
                </div>

                <div class="mb-3">
                    <strong>Message</strong>
                    <pre class="log-detail-block">{{ selectedLog.message }}</pre>
                </div>

                <div v-if="hasContext" class="mb-3">
                    <strong>Context</strong>
                    <pre class="log-detail-block">{{ formattedContext }}</pre>
                </div>

                <div v-if="selectedLog.stacktrace">
                    <strong>Stack Trace</strong>
                    <pre class="log-detail-block log-stacktrace">{{ selectedLog.stacktrace }}</pre>
                </div>
            </div>

            <template v-slot:modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('log-detail-modal')">Close</b-button>
            </template>
        </b-modal>
    </b-container>
</template>

<script>
export default {
    name: 'Logs',

    data() {
        return {
            filters: {
                date: this.todayDate(),
                type: 'all',
            },
            typeOptions: [
                { value: 'all', text: 'All' },
                { value: 'exception', text: 'Exception' },
                { value: 'error', text: 'Error' },
                { value: 'warning', text: 'Warning' },
                { value: 'info', text: 'Info' },
            ],
            table: {
                isBusy: false,
                perPage: 20,
                currentPage: 1,
                totalRows: 0,
                pageOptions: [10, 20, 50, 100],
                items: [],
            },
            fields: [
                { key: 'datetime', label: 'Date/Time', sortable: false },
                { key: 'level', label: 'Level', sortable: false },
                { key: 'type', label: 'Type', sortable: false },
                { key: 'source', label: 'Source', sortable: false },
                { key: 'message', label: 'Message', sortable: false },
            ],
            selectedLog: null,
            truncated: false,
        }
    },

    computed: {
        hasContext() {
            return this.selectedLog
                && this.selectedLog.context
                && Object.keys(this.selectedLog.context).length > 0
        },

        formattedContext() {
            if (!this.hasContext) {
                return ''
            }

            return JSON.stringify(this.selectedLog.context, null, 2)
        },
    },

    methods: {
        todayDate() {
            const now = new Date()
            const month = String(now.getMonth() + 1).padStart(2, '0')
            const day = String(now.getDate()).padStart(2, '0')

            return `${now.getFullYear()}-${month}-${day}`
        },

        refreshLogs() {
            this.table.currentPage = 1
            this.$root.$emit('bv::refresh::table', 'logs-table')
        },

        logsProvider(ctx) {
            this.table.isBusy = true

            return axios.get('/api/company/logs', {
                params: {
                    date: this.filters.date,
                    type: this.filters.type,
                    page: ctx.currentPage,
                    perPage: ctx.perPage,
                },
            }).then((res) => {
                this.table.items = res.data.logs || []
                this.table.totalRows = res.data.total || 0
                this.truncated = res.data.truncated || false
                this.table.isBusy = false

                return this.table.items
            }).catch(() => {
                this.table.isBusy = false
                this.table.items = []
                this.table.totalRows = 0

                return []
            })
        },

        onRowSelected(items) {
            if (!items || items.length === 0) {
                return
            }

            this.selectedLog = items[0]
            this.$bvModal.show('log-detail-modal')
        },

        formatDatetime(value) {
            if (!value) {
                return '—'
            }

            return value
        },

        truncateMessage(message) {
            if (!message) {
                return ''
            }

            const singleLine = message.replace(/\s+/g, ' ').trim()

            if (singleLine.length <= 120) {
                return singleLine
            }

            return singleLine.substring(0, 120) + '…'
        },

        levelVariant(level) {
            const normalized = (level || '').toUpperCase()

            if (['ERROR', 'CRITICAL', 'ALERT', 'EMERGENCY'].includes(normalized)) {
                return 'danger'
            }

            if (normalized === 'WARNING') {
                return 'warning'
            }

            if (normalized === 'INFO') {
                return 'info'
            }

            return 'secondary'
        },
    },
}
</script>

<style scoped>
.log-message-preview {
    cursor: pointer;
}

.log-detail-block {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 12px;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 0.85rem;
    max-height: 300px;
    overflow: auto;
}

.log-stacktrace {
    max-height: 400px;
}
</style>
